/**
 * Driving routes, with the time to reach every point along them.
 *
 * The planner needs more than "how long": it needs `R(s)`, the drive time from
 * the start to each position on the route, so that a church can be timed from
 * where it joins the route. Valhalla returns the shape with per-maneuver times,
 * which are spread over each maneuver's stretch of shape by distance. OSRM is
 * the fallback and reports per-segment durations directly.
 *
 * Both are public demo servers on OpenStreetMap data, in free-flowing traffic.
 * Google's own estimate (with traffic) will differ; the planner absorbs that
 * with its arrival buffer and an optional safety margin.
 */

import { decodePolyline, haversineKm, type LatLng } from "@viamissa/geo";
import { getJson, postJson, type RequestOptions } from "@viamissa/http";

const VALHALLA = "https://valhalla1.openstreetmap.de/route";
const OSRM = "https://router.project-osrm.org/route/v1/driving";

export const ATTRIBUTION = "Routing by Valhalla and OSRM / OpenStreetMap contributors";

export type { LatLng };

export type Avoid = { highways: boolean; tolls: boolean; ferries: boolean };

export type Route = {
  /** The road geometry, start to finish. */
  shape: LatLng[];
  /** Kilometres from the start to each shape point. */
  cumKm: number[];
  /** Driving minutes from the start to each shape point. */
  cumMin: number[];
  /** Shape index at which each stop (origin, waypoints, destination) is reached. */
  stopIndices: number[];
  totalKm: number;
  totalMin: number;
  source: "valhalla" | "osrm";
  /** False when the fallback router could not apply the avoid options. */
  avoidHonoured: boolean;
};

export type RouteQuery = {
  stops: LatLng[];
  avoid?: Avoid;
  http?: RequestOptions;
};

type ValhallaManeuver = { time?: number; begin_shape_index?: number; end_shape_index?: number };
type ValhallaLeg = { shape?: string; maneuvers?: ValhallaManeuver[] };
type ValhallaResponse = { trip?: { legs?: ValhallaLeg[] } };

const segmentKm = (shape: LatLng[]): number[] =>
  shape.slice(1).map((point, index) => haversineKm(shape[index]!, point));

/**
 * Minutes to each shape point of one leg: each maneuver's time is shared out
 * over its own segments in proportion to their length.
 */
export function legTimes(shape: LatLng[], maneuvers: ValhallaManeuver[]): number[] {
  const lengths = segmentKm(shape);
  const perSegment = new Array<number>(lengths.length).fill(0);
  for (const maneuver of maneuvers) {
    const begin = maneuver.begin_shape_index ?? 0;
    const end = Math.min(maneuver.end_shape_index ?? begin, lengths.length);
    const seconds = maneuver.time ?? 0;
    if (end <= begin) continue;
    const span = lengths.slice(begin, end).reduce((sum, km) => sum + km, 0);
    for (let index = begin; index < end; index++) {
      const share = span > 0 ? (lengths[index] ?? 0) / span : 1 / (end - begin);
      perSegment[index] = (perSegment[index] ?? 0) + (seconds * share) / 60;
    }
  }
  const cumulative = [0];
  for (const minutes of perSegment) cumulative.push(cumulative[cumulative.length - 1]! + minutes);
  return cumulative;
}

/** Join legs into one route, dropping each later leg's repeated first point. */
export function joinLegs(legs: { shape: LatLng[]; minutes: number[] }[], source: Route["source"], avoidHonoured: boolean): Route {
  const shape: LatLng[] = [];
  const cumMin: number[] = [];
  const stopIndices: number[] = [0];
  let offset = 0;
  legs.forEach((leg, legIndex) => {
    const skip = legIndex === 0 ? 0 : 1;
    for (let index = skip; index < leg.shape.length; index++) {
      shape.push(leg.shape[index]!);
      cumMin.push(offset + (leg.minutes[index] ?? 0));
    }
    offset = cumMin[cumMin.length - 1] ?? offset;
    stopIndices.push(shape.length - 1);
  });
  const cumKm = [0];
  for (const km of segmentKm(shape)) cumKm.push(cumKm[cumKm.length - 1]! + km);
  return {
    shape,
    cumKm,
    cumMin,
    stopIndices,
    totalKm: cumKm[cumKm.length - 1] ?? 0,
    totalMin: cumMin[cumMin.length - 1] ?? 0,
    source,
    avoidHonoured,
  };
}

function costingOptions(avoid: Avoid | undefined) {
  if (!avoid) return {};
  return {
    auto: {
      ...(avoid.highways ? { use_highways: 0 } : {}),
      ...(avoid.tolls ? { use_tolls: 0 } : {}),
      ...(avoid.ferries ? { use_ferry: 0 } : {}),
    },
  };
}

export async function fetchValhallaRoute(query: RouteQuery): Promise<Route> {
  const data = await postJson<ValhallaResponse>(
    VALHALLA,
    {
      locations: query.stops.map((stop) => ({ lat: stop.lat, lon: stop.lng, type: "break" })),
      costing: "auto",
      costing_options: costingOptions(query.avoid),
      directions_options: { units: "kilometers" },
    },
    query.http,
  );
  const legs = (data.trip?.legs ?? []).map((leg) => {
    const shape = decodePolyline(leg.shape ?? "", 6);
    return { shape, minutes: legTimes(shape, leg.maneuvers ?? []) };
  });
  if (legs.length === 0 || legs.some((leg) => leg.shape.length < 2)) throw new Error("Valhalla returned no route");
  return joinLegs(legs, "valhalla", true);
}

type OsrmResponse = {
  routes?: { legs?: { annotation?: { duration?: number[] } }[]; geometry?: string }[];
};

export async function fetchOsrmRoute(query: RouteQuery): Promise<Route> {
  const coordinates = query.stops.map((stop) => `${stop.lng.toFixed(6)},${stop.lat.toFixed(6)}`).join(";");
  const data = await getJson<OsrmResponse>(
    `${OSRM}/${coordinates}?overview=full&geometries=polyline6&annotations=duration`,
    query.http,
  );
  const route = data.routes?.[0];
  const full = decodePolyline(route?.geometry ?? "", 6);
  if (!route || full.length < 2) throw new Error("OSRM returned no route");

  // OSRM's overview is one line; its legs' annotations split it, one entry per segment.
  const legs: { shape: LatLng[]; minutes: number[] }[] = [];
  let start = 0;
  for (const leg of route.legs ?? []) {
    const durations = leg.annotation?.duration ?? [];
    const shape = full.slice(start, start + durations.length + 1);
    const minutes = [0];
    for (const seconds of durations) minutes.push(minutes[minutes.length - 1]! + seconds / 60);
    legs.push({ shape, minutes });
    start += durations.length;
  }
  const avoiding = !!query.avoid && (query.avoid.highways || query.avoid.tolls || query.avoid.ferries);
  return joinLegs(legs, "osrm", !avoiding);
}

/** Route through the stops in order: Valhalla, then OSRM if Valhalla fails. */
export async function fetchRoute(query: RouteQuery): Promise<Route> {
  try {
    return await fetchValhallaRoute(query);
  } catch {
    return fetchOsrmRoute(query);
  }
}

/** Drive time from the start to a fractional shape position (`index + t`). */
export function minutesAt(route: Route, index: number, t = 0): number {
  const here = route.cumMin[index] ?? route.totalMin;
  const next = route.cumMin[index + 1] ?? here;
  return here + (next - here) * t;
}

/**
 * Exact out-and-back detour from a point on the route to a church: minutes
 * there and minutes back. `null` when no router answers.
 */
export async function fetchDetour(
  from: LatLng,
  church: LatLng,
  options: { avoid?: Avoid; http?: RequestOptions } = {},
): Promise<{ toMin: number; backMin: number } | null> {
  try {
    const route = await fetchRoute({ stops: [from, church, from], avoid: options.avoid, http: options.http });
    const middle = route.stopIndices[1] ?? 0;
    const toMin = route.cumMin[middle] ?? 0;
    return { toMin, backMin: route.totalMin - toMin };
  } catch {
    return null;
  }
}

const coordinate = (point: LatLng) => `${point.lat.toFixed(6)},${point.lng.toFixed(6)}`;

/**
 * A Google Maps directions link through the given stops, using the documented
 * Maps URLs API so it deep-links into the mobile apps. That API has no
 * departure time or avoid options, so the page shows those beside the button.
 */
export function googleMapsDirectionsUrl(stops: LatLng[]): string {
  const first = stops[0];
  const last = stops[stops.length - 1];
  if (!first || !last) return "https://www.google.com/maps";
  const params = new URLSearchParams({
    api: "1",
    origin: coordinate(first),
    destination: coordinate(last),
    travelmode: "driving",
  });
  const waypoints = stops.slice(1, -1).map(coordinate);
  if (waypoints.length) params.set("waypoints", waypoints.join("|"));
  return `https://www.google.com/maps/dir/?${params}`;
}
